import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useOs } from '@wppopen/react'
import { AxiosError } from 'axios'
import { jwtDecode } from 'jwt-decode'
import { useEffect, useMemo } from 'react'

import { QueryFetcher, QueryFetcherParams, QueryFetcherResponse } from 'api/common/types'
import { useAZToken } from 'hooks/useAZToken'
import { isTokenExpired } from 'utils'

export function createUseQuery<F extends QueryFetcher, S>({
  queryKey,
  fetcher,
  selector,
}: {
  queryKey: string
  fetcher: F
  selector: (response?: QueryFetcherResponse<F>) => S
}) {
  return function useCustomQuery(
    options: { params?: QueryFetcherParams<F> } & Omit<
      UseQueryOptions<QueryFetcherResponse<F>, AxiosError, QueryFetcherResponse<F>, [string, QueryFetcherParams<F>]>,
      'queryKey' | 'queryFn' | 'queryHash' | 'queryKeyHashFn' | 'select'
    > = {},
  ) {
    const { osApi } = useOs()
    const { azAccessToken, refreshToken } = useAZToken(osApi.getAccessToken())

    const decodedToken = azAccessToken ? jwtDecode(azAccessToken) : null
    const isAZExpired = !decodedToken ? true : isTokenExpired(decodedToken?.exp)
    const { params = {} as any, enabled = true, ...queryOptions } = options
    const realEnabled = enabled && !isAZExpired
    const { data, isLoading, ...rest } = useQuery({
      queryKey: [queryKey, params],
      ...{ enabled: realEnabled, ...queryOptions },
      queryFn: ({ signal }: { signal: any }) => fetcher(params)(signal) as QueryFetcherResponse<F>,
    })

    useEffect(() => {
      if (isAZExpired || !azAccessToken) {
        refreshToken()
      }
    }, [isAZExpired, azAccessToken, refreshToken])

    return {
      ...rest,
      isLoading: realEnabled && isLoading,
      data: useMemo(() => selector(data), [data]),
      response: data,
    }
  }
}
